import type {
  UptimeBucket,
  UptimeBucketCounts,
} from "@/v2-backoffice-client/uptime/uptime.buckets.utils"

// PAGE_SIZE matches the existing default chunk size of the
// uptime tables (the previous client-side infinite-scroll chunked
// rows in 50s); keeping it here means the server-side paginated
// path renders the same "first page" footprint operators are used
// to. Bumping this is a one-place change.
export const PAGE_SIZE = 50

// URL keys for the segmented status pill on the two uptime tables.
// Two distinct keys so a saved URL targets exactly one of the tables
// even if both end up mounted side-by-side in the future.
export const URL_PARAM_INCIDENT = "incidentStatus" as const
export const URL_PARAM_CATEGORY = "categoryStatus" as const

// Set of accepted bucket values for the URL handoff. Anything
// outside this set is ignored on read, defaulting back to "all" so
// a stale or hand-edited URL never lands the table in a weird
// "no rows visible, no pill highlighted" state.
export const VALID_BUCKETS: ReadonlySet<string> = new Set([
  "all",
  "down",
  "slow",
  "drift",
  "muted",
])

// Reads the active bucket out of the URL synchronously so the
// table's row filter, the segmented pill UI and the URL all agree
// on first paint. Default is "all" -- the previous "auto-bias to
// the most actionable bucket" heuristic was responsible for the
// "pill says All but only down rows render" sync bug, so we now
// keep the URL as the single source of truth.
export function readBucketFromUrl(
  searchParams: URLSearchParams,
  urlParam: string,
): UptimeBucket {
  const raw = searchParams.get(urlParam)
  if (raw && VALID_BUCKETS.has(raw)) return raw as UptimeBucket
  return "all"
}

// Empty-counts placeholder used while the first response is in
// flight. Keeping it exported (and a singleton) means every render
// before data arrives uses the same object reference -- callers
// that do `useMemo(... , [counts])` don't churn cache entries on
// every paint.
export const EMPTY_UPTIME_BUCKET_COUNTS: UptimeBucketCounts = {
  all: 0,
  down: 0,
  slow: 0,
  drift: 0,
  muted: 0,
}
